import React, { useEffect, useState } from 'react';
import { Switch, useHistory, useLocation } from 'react-router-dom';
import { IntlProvider } from 'react-intl';
import './App.css';
import 'antd/dist/antd.css';
import './bootstrap/css/bootstrap.min.css';
import 'react-bootstrap-table-next/dist/react-bootstrap-table2.min.css';
import './index.scss';
import './padding.scss';
import './placeholder-loading.css';
import './table.scss';
import { useDispatch, useSelector } from 'react-redux';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { notification } from 'antd';
import {
  PrivateRoute,
  LoggedOutRoute,
  CheckToken,
  PublicRoute,
  FreeRoute,
} from './components/Auth/Auth';
import {
  HOME,
  SIGNIN,
  PROFILES,
  PROFILES_DETAIL,
  ERROR,
  PROFILE,
  SAVED_SEARCHES,
  SAVED_PROFILES,
  RESULTS,
  SCREENING,
  SCREENING_SEARCHES,
  SCREENING_SUCCESS,
  SEARCH_DASHBOARD,
  CREATE_TEMPLATE,
  SCREENINGS_PROFILE,
  INVITED_PROFILES_DETAIL,
  CREATE_TEST_GROUP,
  NEW_SEARCH_SEND,
  CREATE_ABM_TESTGROUP_SCREENING,
  EDIT_TEST_GROUP,
  INVITED_PROFILES_PROCTORING_DETAIL,
  TESTS,
  NEW_SEARCH_SUCCESS,
  RESET_TEST_GROUP,
  CREATE_EVALUATION,
  REGISTER,
  FORGOTPASSWORD,
  WORKSPACE,
  CREATED_WORKSPACE,
  CONFIGURATION,
  CONFIGURATION_MEMBERS,
  PLANS_CONSUMPTIONS,
  PLAN_PAYMENT_VALIDATE,
  PAYMENT_STATUS,
  PAYMENT_SUCCESS,
  PAYMENT_PENDING,
  PAYMENT_FAILURE,
  CREATE_JOB_POST,
  DETAIL_JOB_POST,
  VIEW_ALL_JOB_POST,
  EDIT_JOB_POST,
  CERTIFICATES,
  NOTIFICATIONS,
  CATEGORIES_TEST,
  SUBSKILLS_TEST,
  ROLES_TESTGROUP,
  USERS_SUMMARY,
  CUSTOM_EMAILS,
  TRANSLATE_TEST_GROUP,
  PUBLIC_DASHBOARD,
  PUBLIC_INVITED_PROFILES_DETAIL,
  PUBLIC_DASHBOARD_BASE_URL,
  PUBLIC_INVITED_PROFILES_PROCTORING_DETAIL,
  INTEGRATIONS,
  BULK_UPDATE_TESTS,
  CHAT,
  ALKYMATION,
} from './constants/routes';
import Searches from './pages/Searches/Searches';
import { determinateParameter } from './utils/determinateParameter';
import { auth0Client } from './auth0/auth0';
import Favorites from './pages/Favorites';
import CookieConsentBanner from './components/CookieConsentBanner/CookieConsentBanner';
import SignIn from './pages/SignIn';
import ProfilesIndex from './pages/ProfilesIndex';
import CompanyProfile from './pages/CompanyProfile';
import NotFound from './pages/NotFound';
import Home from './pages/Home';
import MyError from './components/MyError/MyError';
import Error from './pages/Error/Error';
import useTranslations from './hooks/useTranslations';
import Results from './pages/Results';
import Screening from './pages/Screening/Screening';
import Certificates from './pages/Certificates/Certificates';
import ScreeningSearches from './pages/ScreeningSearches/ScreeningSearches';
import {
  SetAuthenticated,
  setCurrentRole,
  setCurrentWorkspace,
  setIsPublicDashboardState,
  setLoadingMe,
  setPublicDashboardGuest,
  setUserEmail,
  setUserToken,
  setVerify,
  setVerifyEmailUser,
} from './redux/actions/authActions';
import { Window } from './utils/window';
import Search from './pages/Search/Search';
import ScreeningSent from './pages/ScreeningSent/ScreeningSent';
import CreateTemplate from './pages/CreateTemplate/CreateTemplate';
import ScreeningProfile from './pages/ScreeningProfile/ScreeningProfile';
import InvitedProfile from './pages/InvitedProfile/InvitedProfile';
import useRoles from './hooks/useRoles';
import CreateTestGroup from './pages/CreateTestGroup/CreateTestGroup';
import NewSearchSend from './pages/NewSearchSend/NewSearchSend';
import Tests from './pages/Tests/Tests';
import useSidebar from './hooks/useSidebar';
import ABMScreeningsTestGroups from './pages/ABMScreeningsTestgroups/ABMScreeningsTestgroups';
import CandidateProctoringDetail from './pages/CandidateProctoringDetail/CantidateProctoringDetail';
import N8nChat from './components/N8nChat';
import NewSearchSuccess from './pages/NewSearchSuccess/NewSearchSuccess';
import ResetTestgroups from './pages/ResetTestgroups/ResetTestgroups';
import CreateEvaluation from './pages/CreateEvaluation/CreateEvaluation';
import Workspace from './pages/Workspace/Workspace';
import CreatedWorkspace from './pages/CreatedWorkspace/CreatedWorkspace';
import MembersConfiguration from './pages/MembersConfiguration/MembersConfiguration';
import Plans from './pages/Plans/Plans';
import PaymentValidation from './pages/Plans/PaymentValidation/PaymentValidation';
import CreateJobPost from './pages/Jobs/CreateJobPost/CreateJobPost';
import Jobs from './pages/Jobs/Jobs';
import { acceptWorkspaceInvitationsBatch } from './API/services/companyService';
import Personalize from './pages/Settings/Personalize';
import { getLocalStorageWorkspace, setLocalStorageWorkspace } from './utils/workspaceUtils';
import EditJobPost from './pages/Jobs/EditJobPost/EditJobPost';
import DetailJobPost from './pages/Jobs/DetailJobPost/DetailJobPost';
import { PartnerWorkspaceRole } from './constants/enums';
import Success from './pages/Payments/Success';
import Pending from './pages/Payments/Pending';
import Failure from './pages/Payments/Failure';
import Status from './pages/Payments/Status';
import NotificationPage from './pages/Notification/NotificationPage';
import CategoriesPage from './pages/Categories/CategoriesPage';
import SubskillsPage from './pages/Subskills/SubskillsPage';
import RolesTestGroupPage from './pages/RolesTestGroup/RolesTestGroupPage';
import UserSummary from './pages/UsersSummary/UsersSummary';
import CustomEmails from './pages/CustomEmails/CustomEmails';
import TranslateTestGroup from './pages/TranslateTestGroup/TranslateTestGroup';
import { handleAuthResult } from './auth0/customOTPLogin';
import { decodeToken } from './API/services/authService';
import { t } from './i18n/i18n';
import PublicDashboard from './components/PublicDashboard/PublicDashboard';
import { guestData } from './API/services/dashboardInvitation';
import BulkUpdateTests from './pages/BulkUpdateTests/BulkUpdateTests';
import ErrorBoundary from './components/ErrorBoundary/ErrorBoundary.jsx';
import { NotificationProvider } from './contexts/NotificationContext';
import NotificationAlert from './components/NotificationAlert/NotificationAlert';
import NPSModal from './components/NPSModal/NPSModal';
import { useNPSModal } from './hooks/useNPSModal';
import { TrackEventGTM } from './utils/filters/analyticsUtils';
import Chat from './pages/Chat/Chat';
import AlkyMation from './pages/AlkyMation/AlkyMation';
import { EventsGTM } from './constants/events.js';
import Integrations from './pages/integrations/Integrations';

const App = () => {
  const history = useHistory();
  let prevPath = '';
  const location = useLocation();

  const { language, key } = useTranslations();
  const [screeningTestCondition, setScreeningTestCondition] = useState(true);
  const [createTestGroupCondition, setCreateTestGroupCondition] = useState(true);
  const [resetTestGroupCondition, setResetTestGroupCondition] = useState(true);
  const [translateTestGroupCondition, setTranslateTestGroupCondition] = useState(true);
  const {
    user,
    currentWorkspace,
    isPublicDashboard,
    publicDashboardGuest,
    authenticated: isAuthenticated,
  } = useSelector((state) => state.auth);

  const { roles } = useRoles();
  const { setCollapsed } = useSidebar();
  const dispatch = useDispatch();

  useNPSModal();

  const handleNPSSuccess = (npsData) => {
    TrackEventGTM(EventsGTM.NPS_SUBMITTED, {
      event_category: npsData.processType,
      event_action: 'click',
      score: npsData.score,
      processType: npsData.processType,
      hasComment: !!npsData.comment,
    });
  };

  const handleNPSError = () => {
    notification.error({
      message: t('NPS_ERROR_TITLE', 'Error al enviar evaluación'),
      description: t(
        'NPS_ERROR_MESSAGE',
        'No pudimos enviar tu evaluación. Por favor, inténtalo de nuevo.'
      ),
    });
  };

  useEffect(() => {
    if (Window()) {
      const rolesParsed = JSON.parse(localStorage.getItem('roles'));
      setCreateTestGroupCondition(rolesParsed?.includes(roles.TESTGROUPCREATOR));
      setScreeningTestCondition(rolesParsed?.includes(roles.TESTSCREENINGSPARTNER));
      setResetTestGroupCondition(rolesParsed?.includes(roles.TESTGROUPRESTARTER));
      setTranslateTestGroupCondition(rolesParsed?.includes(roles.TRANSLATOR));
      if (JSON.parse(localStorage.getItem('isOnboarding')) === true) {
        setCollapsed(false);
      }
      if (user && user.workspaces.length !== 0) {
        let indexWorkspace;
        const workspaceId = Window().localStorage.getItem('workspaceIdRedirect');
        localStorage.removeItem('workspaceIdRedirect');
        if (workspaceId && workspaceId !== currentWorkspace) {
          indexWorkspace = user.workspaces.findIndex(
            (workspace) => workspace.workspaceId === Number(workspaceId)
          );
        }
        if (indexWorkspace >= 0) {
          dispatch(setCurrentRole(user.workspaces[indexWorkspace]?.role));
          dispatch(setCurrentWorkspace(workspaceId));
          setLocalStorageWorkspace(user?.workspaces[indexWorkspace]);
        } else if (workspaceId) {
          notification.open({
            message: t('APP_ERROR_NOT_ACCESS_WORKSPACE_FOR_SEARCH'),
            type: 'error',
          });
          history.push(HOME);
        }
      }
    }
  }, [user]);

  useEffect(() => {
    if (Window()) {
      if (user && user.workspaces.length !== 0) {
        if (currentWorkspace) {
          const indexWorkspace = user.workspaces.findIndex(
            (workspace) => workspace.workspaceId === Number(currentWorkspace)
          );
          setLocalStorageWorkspace(user?.workspaces[indexWorkspace]);
          dispatch(setCurrentRole(user?.workspaces[indexWorkspace]?.role));
        } else if (getLocalStorageWorkspace()) {
          const workspaceData = getLocalStorageWorkspace();
          dispatch(setCurrentWorkspace(workspaceData?.id));
          dispatch(
            setCurrentRole(
              user.workspaces.filter((workspace) => workspace.workspaceId === workspaceData?.id)[0]
                ?.role
            )
          );
        } else {
          dispatch(setCurrentWorkspace(user.workspaces[0]?.workspaceId));
          dispatch(setCurrentRole(user.workspaces[0]?.role));
          setLocalStorageWorkspace(user?.workspaces[0]);
        }
      }
    }
  }, [user, currentWorkspace]);

  useEffect(() => {
    const handleSetPublicDashboardGuest = async () => {
      if (!publicDashboardGuest && isAuthenticated) {
        try {
          const recruitSessionId = location?.pathname?.split('/')[2];
          const publicDashboardGuest = await guestData(recruitSessionId);
          dispatch(setPublicDashboardGuest(publicDashboardGuest));
        } catch (error) {
          notification.error({
            message: t('PUBLIC_DASHBOARD_ERROR_LOADING_DATA'),
            description: t('PUBLIC_DASHBOARD_ERROR_LOADING_DATA_DESCRIPTION'),
            placement: 'topRight',
          });
        }
      }
    };

    if (history.location.pathname.includes(PUBLIC_DASHBOARD_BASE_URL)) {
      dispatch(setIsPublicDashboardState(true));
      handleSetPublicDashboardGuest();
    }
  }, [isAuthenticated, publicDashboardGuest, history.location.pathname]);

  useEffect(() => {
    const token = localStorage.getItem('token');
    const checkOTPSession = async () => {
      try {
        const response = await decodeToken();
        const result = {
          id_token: response,
          access_token: token,
        };
        await handleAuthResult({ result, dispatch, roles, setCollapsed, isPublicDashboard });
      } catch {
        dispatch(SetAuthenticated(false));
        dispatch(setLoadingMe(false));
        dispatch(setVerify(false));
      }
    };
    if (Window()) {
      const idToken = JSON.parse(localStorage.getItem('id_token'));
      if (idToken && token) {
        checkOTPSession();
      } else {
        auth0Client().checkSession(
          {
            audience: process.env.RAZZLE_RUNTIME_AUTH_ZERO_AUDIENCE,
            scope: 'read:current_user',
          },
          (err, result) => {
            (async () => {
              if (err === null && result.accessToken) {
                localStorage.setItem('token', result.accessToken);
                localStorage.setItem(
                  'roles',
                  JSON.stringify(
                    result.idTokenPayload[`${process.env.RAZZLE_RUNTIME_AUTH_ZERO_AUDIENCE}roles`]
                  )
                );
                dispatch(SetAuthenticated(true));
                dispatch(
                  setVerify(
                    result.idTokenPayload[
                      `${process.env.RAZZLE_RUNTIME_AUTH_ZERO_AUDIENCE}email_verified`
                    ]
                  )
                );
                dispatch(
                  setVerifyEmailUser(
                    result.idTokenPayload[`${process.env.RAZZLE_RUNTIME_AUTH_ZERO_AUDIENCE}email`]
                  )
                );
                dispatch(setUserToken(result.accessToken));
                dispatch(
                  setUserEmail(
                    result.idTokenPayload[`${process.env.RAZZLE_RUNTIME_AUTH_ZERO_AUDIENCE}email`]
                  )
                );
                if (
                  JSON.parse(localStorage.getItem('isOnboarding')) === null &&
                  result.idTokenPayload[
                    `${process.env.RAZZLE_RUNTIME_AUTH_ZERO_AUDIENCE}roles`
                  ]?.includes(roles?.TESTSCREENINGSPARTNER)
                ) {
                  setCollapsed(false);
                  localStorage.setItem('isOnboarding', false);
                }
                await acceptWorkspaceInvitationsBatch();
                dispatch(setLoadingMe(false));
              } else {
                dispatch(SetAuthenticated(false));
                dispatch(setLoadingMe(false));
                dispatch(setVerify(false));
              }
            })();
          }
        );
      }
    }
  }, []);

  const isWorkspaceAdminOrOwner = [
    PartnerWorkspaceRole.ADMINISTRATOR,
    PartnerWorkspaceRole.OWNER,
  ].includes(getLocalStorageWorkspace()?.role);

  const queryClient = new QueryClient();
  return (
    <ErrorBoundary>
      <QueryClientProvider client={queryClient}>
        <NotificationProvider>
          <IntlProvider messages={language} locale={key} onError={() => {}}>
            <CookieConsentBanner />
            <CheckToken>
              <Switch className="switch-reset">
                <PrivateRoute exact path={HOME}>
                  <Home />
                </PrivateRoute>
                <PrivateRoute exact path={WORKSPACE}>
                  <Workspace />
                </PrivateRoute>
                <PrivateRoute exact path={CONFIGURATION}>
                  <Personalize />
                </PrivateRoute>
                <PrivateRoute exact path={CONFIGURATION_MEMBERS}>
                  <MembersConfiguration />
                </PrivateRoute>
                <PrivateRoute exact path={PLANS_CONSUMPTIONS}>
                  <Plans />
                </PrivateRoute>
                <PrivateRoute exact path={PLAN_PAYMENT_VALIDATE}>
                  <PaymentValidation />
                </PrivateRoute>
                <PrivateRoute exact path={PAYMENT_STATUS}>
                  <Status />
                </PrivateRoute>
                <PrivateRoute exact path={PAYMENT_SUCCESS}>
                  <Success />
                </PrivateRoute>
                <PrivateRoute exact path={PAYMENT_PENDING}>
                  <Pending />
                </PrivateRoute>
                <PrivateRoute exact path={PAYMENT_FAILURE}>
                  <Failure />
                </PrivateRoute>
                <PrivateRoute exact path={PROFILES_DETAIL}>
                  <InvitedProfile isInvitedProfile={false} />
                </PrivateRoute>
                <PrivateRoute exact path={INVITED_PROFILES_DETAIL}>
                  <InvitedProfile isInvitedProfile />
                </PrivateRoute>
                {process.env.RAZZLE_RUNTIME_IS_PROCTORING_ENABLED === 'true' && (
                  <PrivateRoute exact path={INVITED_PROFILES_PROCTORING_DETAIL}>
                    <CandidateProctoringDetail isInvitedProfile />
                  </PrivateRoute>
                )}
                <PrivateRoute exact path={WORKSPACE}>
                  <Workspace />
                </PrivateRoute>
                <PrivateRoute exact path={CONFIGURATION}>
                  <Personalize />
                </PrivateRoute>
                <PrivateRoute exact path={CONFIGURATION_MEMBERS}>
                  <MembersConfiguration />
                </PrivateRoute>
                <PrivateRoute exact path={PLANS_CONSUMPTIONS}>
                  <Plans />
                </PrivateRoute>
                <PrivateRoute exact path={PLAN_PAYMENT_VALIDATE}>
                  <PaymentValidation />
                </PrivateRoute>
                <PrivateRoute exact path={PAYMENT_STATUS}>
                  <Status />
                </PrivateRoute>
                <PrivateRoute exact path={PAYMENT_SUCCESS}>
                  <Success />
                </PrivateRoute>
                <PrivateRoute exact path={ALKYMATION}>
                  <AlkyMation />
                </PrivateRoute>
                <PrivateRoute exact path={PAYMENT_PENDING}>
                  <Pending />
                </PrivateRoute>
                <PrivateRoute exact path={PAYMENT_FAILURE}>
                  <Failure />
                </PrivateRoute>
                <PrivateRoute exact path={PROFILES_DETAIL}>
                  <InvitedProfile isInvitedProfile={false} />
                </PrivateRoute>
                <PrivateRoute exact path={INVITED_PROFILES_DETAIL}>
                  <InvitedProfile isInvitedProfile />
                </PrivateRoute>
                {process.env.RAZZLE_RUNTIME_IS_PROCTORING_ENABLED === 'true' && (
                  <PrivateRoute exact path={INVITED_PROFILES_PROCTORING_DETAIL}>
                    <CandidateProctoringDetail isInvitedProfile />
                  </PrivateRoute>
                )}
                <PrivateRoute exact path={PROFILES}>
                  <ProfilesIndex />
                </PrivateRoute>
                <PrivateRoute exact path={PROFILE}>
                  <CompanyProfile />
                </PrivateRoute>
                <PrivateRoute exact path={NOTIFICATIONS}>
                  <NotificationPage />
                </PrivateRoute>
                <PrivateRoute exact path={SCREENINGS_PROFILE}>
                  <ScreeningProfile />
                </PrivateRoute>
                <PrivateRoute exact path={SAVED_SEARCHES}>
                  <Searches />
                </PrivateRoute>
                <PrivateRoute exact path={SAVED_PROFILES}>
                  <Favorites />
                </PrivateRoute>
                <PrivateRoute exact path={SCREENING}>
                  <Screening />
                </PrivateRoute>
                <PrivateRoute exact path={SEARCH_DASHBOARD}>
                  <Search />
                </PrivateRoute>
                <PrivateRoute exact path={TESTS}>
                  <Tests />
                </PrivateRoute>
                {isWorkspaceAdminOrOwner && (
                  <PrivateRoute exact path={USERS_SUMMARY}>
                    <UserSummary />
                  </PrivateRoute>
                )}
                <PrivateRoute exact path={CUSTOM_EMAILS}>
                  <CustomEmails />
                </PrivateRoute>
                {createTestGroupCondition && (
                  <PrivateRoute exact path={CREATE_TEST_GROUP}>
                    <CreateTestGroup />
                  </PrivateRoute>
                )}
                {createTestGroupCondition && (
                  <PrivateRoute exact path={EDIT_TEST_GROUP}>
                    <CreateTestGroup />
                  </PrivateRoute>
                )}
                {translateTestGroupCondition && (
                  <PrivateRoute exact path={TRANSLATE_TEST_GROUP}>
                    <TranslateTestGroup />
                  </PrivateRoute>
                )}
                {createTestGroupCondition && (
                  <PrivateRoute exact path={CREATE_ABM_TESTGROUP_SCREENING}>
                    <ABMScreeningsTestGroups />
                  </PrivateRoute>
                )}
                {createTestGroupCondition && (
                  <PrivateRoute exact path={CATEGORIES_TEST}>
                    <CategoriesPage />
                  </PrivateRoute>
                )}
                {createTestGroupCondition && (
                  <PrivateRoute exact path={SUBSKILLS_TEST}>
                    <SubskillsPage />
                  </PrivateRoute>
                )}
                {createTestGroupCondition && (
                  <PrivateRoute exact path={ROLES_TESTGROUP}>
                    <RolesTestGroupPage />
                  </PrivateRoute>
                )}
                {createTestGroupCondition && (
                  <PrivateRoute exact path={BULK_UPDATE_TESTS}>
                    <BulkUpdateTests />
                  </PrivateRoute>
                )}
                {screeningTestCondition && (
                  <PrivateRoute exact path={SCREENING_SEARCHES}>
                    <ScreeningSearches />
                  </PrivateRoute>
                )}
                {screeningTestCondition && (
                  <PrivateRoute exact path={CREATE_EVALUATION}>
                    <CreateEvaluation />
                  </PrivateRoute>
                )}
                {resetTestGroupCondition && (
                  <PrivateRoute exact path={RESET_TEST_GROUP}>
                    <ResetTestgroups />
                  </PrivateRoute>
                )}
                <PrivateRoute exact path={NEW_SEARCH_SEND}>
                  <NewSearchSend />
                </PrivateRoute>
                <PrivateRoute exact path={NEW_SEARCH_SUCCESS}>
                  <NewSearchSuccess />
                </PrivateRoute>
                <PrivateRoute exact path={SCREENING_SUCCESS}>
                  <ScreeningSent />
                </PrivateRoute>
                <PrivateRoute exact path={CREATE_TEMPLATE}>
                  <CreateTemplate />
                </PrivateRoute>
                <PrivateRoute exact path={RESULTS}>
                  <Results />
                </PrivateRoute>
                <PrivateRoute exact path={CREATED_WORKSPACE}>
                  <CreatedWorkspace />
                </PrivateRoute>
                {isWorkspaceAdminOrOwner && (
                  <PrivateRoute exact path={CREATE_JOB_POST}>
                    <CreateJobPost />
                  </PrivateRoute>
                )}
                {isWorkspaceAdminOrOwner && (
                  <PrivateRoute exact path={EDIT_JOB_POST}>
                    <EditJobPost />
                  </PrivateRoute>
                )}
                <PrivateRoute exact path={VIEW_ALL_JOB_POST}>
                  <Jobs />
                </PrivateRoute>
                <PrivateRoute exact path={DETAIL_JOB_POST}>
                  <DetailJobPost />
                </PrivateRoute>
                <PrivateRoute exact path={INTEGRATIONS}>
                  <Integrations />
                </PrivateRoute>
                <PrivateRoute exact path={CHAT}>
                  <Chat />
                </PrivateRoute>
                <LoggedOutRoute exact path={SIGNIN}>
                  <SignIn />
                </LoggedOutRoute>
                <LoggedOutRoute exact path={REGISTER}>
                  <SignIn /> 
                </LoggedOutRoute>
                <LoggedOutRoute exact path={FORGOTPASSWORD}>
                  <SignIn />
                </LoggedOutRoute>
                <PublicRoute exact path={PUBLIC_DASHBOARD}>
                  <PublicDashboard />
                </PublicRoute>
                <PublicRoute exact path={PUBLIC_INVITED_PROFILES_DETAIL}>
                  <InvitedProfile isInvitedProfile />
                </PublicRoute>
                {process.env.RAZZLE_RUNTIME_IS_PROCTORING_ENABLED === 'true' && (
                  <PublicRoute exact path={PUBLIC_INVITED_PROFILES_PROCTORING_DETAIL}>
                    <CandidateProctoringDetail isInvitedProfile />
                  </PublicRoute>
                )}
                <FreeRoute exact path={`${CERTIFICATES}`}>
                  <Certificates />
                </FreeRoute>
                <PublicRoute exact path={`${ERROR}`}>
                  <Error />
                </PublicRoute>
                <PublicRoute exact path={`${ERROR}/:status`}>
                  <MyError />
                </PublicRoute>
                <PublicRoute exact path="/*">
                  <NotFound />
                </PublicRoute>
              </Switch>
              <NotificationAlert />
            </CheckToken>
            <NPSModal onSuccess={handleNPSSuccess} onError={handleNPSError} />
            <N8nChat />
          </IntlProvider>
        </NotificationProvider>
      </QueryClientProvider>
    </ErrorBoundary>
  );
};

export default App;
